import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {includes} from 'ramda';

import {VehicleFinancingBalloonPayment, VehicleFinancingTerm} from '@dms/api/sales';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {RATE_MATRIX_DOWN_PAYMENTS} from '../constants/RATE_MATRIX_DOWN_PAYMENTS';
import {RATE_MATRIX_TERMS} from '../constants/RATE_MATRIX_TERMS';
import {RateMatrixCell} from '../RateMatrixCell/RateMatrixCell';
import {RateMatrixCellData} from '../types/RateMatrixCellData';
import {buildRateMatrixCellKey} from '../utils/buildRateMatrixCellKey';
import {getRateMatrixCellLabel} from '../utils/getRateMatrixCellLabel';
import {isValidBalloonCombination} from '../utils/isValidBalloonCombination';

const CELL_MAX_WIDTH = 33;
const CELL_MIN_WIDTH = 20;
const AXIS_COLUMN_WIDTH = 12;
const GRID_SPACING = 2;

interface RateMatrixGridProps extends RequiredTestIdProps {
  availableTerms: VehicleFinancingTerm[];
  cells: Record<string, RateMatrixCellData>;
  balloonBracket: VehicleFinancingBalloonPayment | Nullish;
  isReadOnly: boolean;
  selectedCellKeys: string[];
  onCellActivate: (
    coordinates: {
      term: VehicleFinancingTerm;
      downPayment: (typeof RATE_MATRIX_DOWN_PAYMENTS)[number];
      cellKey: string;
    },
    isMultiSelect: boolean
  ) => void;
}

export function RateMatrixGrid(props: RateMatrixGridProps) {
  const enabledTerms = RATE_MATRIX_TERMS.filter((term) => includes(term, props.availableTerms));
  const hasEnabledTerms = enabledTerms.length > 0;

  return (
    <Box overflowX="auto" data-testid={suffixTestId('rateMatrixGrid', props)}>
      <VStack spacing={GRID_SPACING}>
        <Show when={hasEnabledTerms}>
          <HStack spacing={GRID_SPACING}>
            <Box width={AXIS_COLUMN_WIDTH} minWidth={AXIS_COLUMN_WIDTH} />
            {RATE_MATRIX_DOWN_PAYMENTS.map((downPayment) => (
              <Box
                key={downPayment}
                width="100%"
                maxWidth={CELL_MAX_WIDTH}
                minWidth={CELL_MIN_WIDTH}
                paddingBottom={1}
              >
                <Text size="xSmall" color="secondary" alternative align="center">
                  {`${downPayment} %`}
                </Text>
              </Box>
            ))}
          </HStack>
        </Show>

        {enabledTerms.map((term) => (
          <HStack key={term} spacing={GRID_SPACING}>
            <Box width={AXIS_COLUMN_WIDTH} minWidth={AXIS_COLUMN_WIDTH}>
              <VStack justify="center" align="center" height="100%">
                <Text size="small" color="secondary" alternative>
                  {term.toString()}
                </Text>
              </VStack>
            </Box>

            {RATE_MATRIX_DOWN_PAYMENTS.map((downPayment) => {
              const coordinates = {term, downPayment, balloonBracket: props.balloonBracket};
              const key = buildRateMatrixCellKey(coordinates);

              return (
                <Box key={key} width="100%" maxWidth={CELL_MAX_WIDTH} minWidth={CELL_MIN_WIDTH}>
                  <RateMatrixCell
                    cellData={props.cells[key]}
                    ariaLabel={getRateMatrixCellLabel(coordinates)}
                    isReadOnly={props.isReadOnly}
                    isDisabled={
                      !isValidBalloonCombination(downPayment, props.balloonBracket ?? null)
                    }
                    isSelected={includes(key, props.selectedCellKeys)}
                    onActivate={(isMultiSelect) =>
                      props.onCellActivate({term, downPayment, cellKey: key}, isMultiSelect)
                    }
                    data-testid={suffixTestId(`cell-[${term}-${downPayment}]`, props)}
                  />
                </Box>
              );
            })}
          </HStack>
        ))}
      </VStack>
    </Box>
  );
}
